import { useEffect, useId, useMemo, useRef, useState } from 'react';

interface ComboboxProduct {
  id: string;
  name: string;
}

interface ProductStockInfo {
  color: string;
  label: string;
  stock: number;
}

interface ProductComboboxProps {
  /** Products to offer. Deliberately NOT filtered by supplier: the comercio may
   *  be buying this article from a new supplier, and hiding the product it
   *  already has is how a duplicate gets created. */
  products: ComboboxProduct[];
  getStockInfo: (productId: string) => ProductStockInfo;
  salesData: Record<string, number>;
  /** Currently selected product id ('' when nothing selected). */
  value: string;
  onSelect: (productId: string) => void;
  /** Accessible name for the input (e.g. "Producto 1"). */
  label: string;
  placeholder?: string;
}

/**
 * Bounded searchable combobox for picking a product inside a form cell.
 * Keyboard: ArrowUp/Down navigate, Enter selects, Esc closes, Tab moves on.
 * Uses the combobox/listbox/option ARIA pattern; the dropdown closes on
 * outside mousedown so the choices themselves stay clickable.
 */
export function ProductCombobox({
  products,
  getStockInfo,
  salesData,
  value,
  onSelect,
  label,
  placeholder = 'Seleccionar producto...',
}: ProductComboboxProps) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const listboxId = useId();

  const selected = products.find((p) => p.id === value) ?? null;
  const info = value ? getStockInfo(value) : null;

  // Sync the input text when the value changes from outside (e.g. form reset).
  useEffect(() => {
    setText(value ? products.find((p) => p.id === value)?.name ?? '' : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const filtered = useMemo(() => {
    const q = text.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => p.name.toLowerCase().includes(q));
  }, [products, text]);

  // Close when clicking outside the control (mousedown, so option clicks win).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the active option visible when navigating with the keyboard.
  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  function choose(productId: string) {
    onSelect(productId);
    setText(products.find((p) => p.id === productId)?.name ?? '');
    setOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setOpen(true);
        setActiveIndex((i) => (filtered.length === 0 ? -1 : (i + 1) % filtered.length));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setOpen(true);
        setActiveIndex((i) =>
          filtered.length === 0 ? -1 : (i - 1 + filtered.length) % filtered.length
        );
        break;
      case 'Enter':
        if (open && activeIndex >= 0 && filtered[activeIndex]) {
          e.preventDefault();
          choose(filtered[activeIndex].id);
        }
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
          setActiveIndex(-1);
        }
        break;
      case 'Tab':
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && activeIndex >= 0 && filtered[activeIndex]
            ? `${listboxId}-${filtered[activeIndex].id}`
            : undefined
        }
        value={text}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setActiveIndex(-1);
          setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        style={{ color: value && info ? info.color : undefined }}
        required
      />
      {open && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={`Opciones de ${label}`}
          className="absolute left-0 right-0 top-full mt-1 z-30 max-h-64 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg"
        >
          {filtered.length === 0 ? (
            <li role="option" aria-disabled="true" className="px-3 py-2 text-sm text-gray-400">
              Sin resultados
            </li>
          ) : (
            filtered.map((p, i) => {
              const s = getStockInfo(p.id);
              const sales = salesData[p.id] ?? 0;
              const active = i === activeIndex;
              const isSelected = p.id === value;
              return (
                <li
                  key={p.id}
                  id={`${listboxId}-${p.id}`}
                  ref={(el) => {
                    optionRefs.current[i] = el;
                  }}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(p.id)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-2 ${
                    active ? 'bg-blue-50 dark:bg-blue-900/30' : ''
                  }`}
                >
                  <span className={`truncate ${isSelected ? 'font-semibold' : ''}`}>{p.name}</span>
                  <span className="text-xs whitespace-nowrap" style={{ color: s.color }}>
                    Stock: {s.stock} ({s.label})
                    {sales > 0 ? ` · ${sales} vendidos` : ''}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}